import { FC, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
} from '@mui/material';

import Iconify from '@src/components/iconify';
import useLocales from '@src/locales/use-locales';
import { NavSubGroupType } from '@src/types/main';

type NavSubGroupMiniProps = {
  group: NavSubGroupType;
};

const NavSubGroupMini: FC<NavSubGroupMiniProps> = (props) => {
  const { group } = props;

  const { t } = useLocales();

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'user info popover' : undefined;

  const location = useLocation();

  const isChildActive = group.entries.some((entry) =>
    location.pathname.startsWith(entry.path)
  );

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={{
          justifyContent: 'flex-end',
          py: 1,
          pr: 0,
          pl: 2,
          gap: 0.5,
          borderRadius: 1,
          backgroundColor: isChildActive ? 'custom.background' : 'transparent',
          '& .MuiListItemIcon-root': {
            minWidth: 'auto',
          },
        }}
      >
        <ListItemIcon>
          <Iconify
            icon={group.icon}
            sx={{ color: isChildActive ? 'primary.dark' : 'custom.lightGrey' }}
          />
        </ListItemIcon>
        <Iconify
          size={18}
          icon="mdi:keyboard-arrow-right"
          sx={{ color: isChildActive ? 'primary.dark' : 'custom.lightGrey' }}
        />
      </ListItemButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <List
          component="div"
          aria-label={t('ariaLabels.subList')}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}
        >
          {group.entries.map((entry) => (
            <ListItemButton
              key={entry.id}
              onClick={handleClose}
              component={NavLink}
              to={entry.path}
              end
              sx={{
                p: 0,
                borderRadius: 1,
                '& .MuiListItemIcon-root': {
                  minWidth: 'auto',
                },
                '&.active': {
                  backgroundColor: 'custom.background',
                  color: 'primary.dark',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                },
              }}
              disableGutters
            >
              <ListItemIcon>
                <Iconify icon={entry.icon} sx={{ color: 'custom.lightGrey' }} />
              </ListItemIcon>
              <ListItemText
                primary={entry.label}
                slotProps={{
                  primary: { sx: { fontFamily: 'secondaryFontFamily' } },
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Popover>
    </>
  );
};

export default NavSubGroupMini;
